/**
 * Settings-page Alpine component for the Premium subscription card.
 *
 * Reads the subscription from GET /v1/plus-status and opens the Stripe Billing
 * Portal (POST /v1/plus-manage), which is where cancellation, auto-renew,
 * invoices and card updates are handled.
 *
 * The portal button is gated on the server-derived `can_manage_billing` alone.
 * Not on `provider`, which only records how the row was last written: comping a
 * paying subscriber rewrites it to `grant` while their Stripe subscription keeps
 * renewing. And not on `enabled`, which is an entitlement fact rather than a
 * billing one: Stripe retries a failed renewal past the end of the paid period,
 * so an entitlement can read inactive while the card is still being charged.
 * Either gate would take the cancel button away from a subscriber Stripe is
 * still billing. Apple and Google subscriptions are managed in their own store,
 * which is what the store note says.
 */
import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';

const PROVIDER_APPLE = 'apple';
const PROVIDER_GOOGLE = 'google';

const REQUEST_TIMEOUT_MS = 15000;

/**
 * This permanent schedule waits 15 seconds between four bounded requests.
 * It prevents the settings page from keeping an open polling loop.
 */
const CHECKOUT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

/**
 * How long to wait for the portal navigation to commit before deciding it was
 * abandoned. Comfortably longer than a redirect to a Stripe-hosted page, so a
 * navigation that is merely slow is never mistaken for one that is not coming.
 */
const NAVIGATION_GRACE_MS = 20000;

/**
 * Bounds a promise that carries no abort signal of its own, so a dependency that
 * hangs surfaces as an error the card can render rather than an endless spinner.
 */
function withTimeout(promise, ms, label) {
    let timer;
    const deadline = new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    });

    return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

/** The site language, which is not the same as the browser's. */
function siteLocale() {
    return document.documentElement.lang || 'en';
}

/** Read only the two return values that our Stripe checkout URLs emit. */
function checkoutResult() {
    const current = new URL(window.location.href);
    const result = current.searchParams.get('checkout');

    return result === 'success' || result === 'cancelled' ? result : null;
}

function clearCheckoutResult() {
    const current = new URL(window.location.href);
    current.searchParams.delete('checkout');
    window.history.replaceState(
        window.history.state,
        '',
        current.pathname + current.search + current.hash,
    );
}

function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * location.assign() executes a `javascript:` URL in our own origin, so the
 * target is required to be https.
 *
 * The host is deliberately not pinned to stripe.com. The URL is minted by our
 * own server from the Stripe SDK and never touches client input, so there is no
 * attack for a host pin to remove, while pinning would strand every subscriber
 * the day the Stripe account enables a custom billing-portal domain.
 */
function portalUrl(value) {
    if (typeof value !== 'string' || value === '') {
        return null;
    }

    let target;
    try {
        target = new URL(value, window.location.origin);
    } catch {
        return null;
    }

    return target.protocol === 'https:' ? target.href : null;
}

/** The body is read inside the timeout, so a stalled body cannot hang the card forever. */
async function readJson(endpoint, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
        const response = await apiClient.request(endpoint, { ...options, signal: controller.signal });
        if (!response.ok) {
            throw new Error(`${endpoint} responded ${response.status}`);
        }

        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

export function premiumSubscription(strings = {}) {
    const messages = {
        loading: strings.loading || '',
        renewsOn: strings.renewsOn || '',
        expiresOn: strings.expiresOn || '',
        appStore: strings.appStore || '',
        playStore: strings.playStore || '',
        granted: strings.granted || '',
        inactive: strings.inactive || '',
        loadError: strings.loadError || '',
        manageError: strings.manageError || '',
        checkoutSuccess: strings.checkoutSuccess || '',
        checkoutPending: strings.checkoutPending || '',
    };

    return {
        loading: true,
        working: false,
        enabled: false,
        planName: '',
        provider: null,
        canManageBilling: false,
        autoRenew: false,
        renewalDate: '',
        errorMessage: '',
        checkoutCompleted: false,

        async init() {
            const result = checkoutResult();
            this.checkoutCompleted = result === 'success';
            if (result === 'cancelled') {
                clearCheckoutResult();
            }

            await this.loadStatus();
            if (this.checkoutCompleted && !this.enabled) {
                await this.refreshAfterCheckout();
            }
            if (this.checkoutCompleted && this.enabled) {
                clearCheckoutResult();
            }

            window.addEventListener('pageshow', async (event) => {
                this.working = false;

                // A back/forward-cache restore does not re-run this component,
                // so without a re-read the card would still assert "Active" and
                // a renewal date to a subscriber who just cancelled in the
                // portal and pressed Back. The re-read is silent so the card
                // does not collapse back to its loading row on a Back.
                if (event.persisted) {
                    this.checkoutCompleted = checkoutResult() === 'success';
                    await this.loadStatus({ silent: true });
                    if (this.checkoutCompleted && this.enabled) {
                        clearCheckoutResult();
                    }
                }
            });
        },

        /** Re-read status while Stripe's webhook updates the entitlement row. */
        async refreshAfterCheckout() {
            for (const delay of CHECKOUT_RETRY_DELAYS_MS) {
                await wait(delay);
                await this.loadStatus({ silent: true, preserveOnError: true });
                if (this.enabled) {
                    return;
                }
            }
        },

        /** Clears every claim the card makes about the subscription. */
        resetSubscription() {
            this.enabled = false;
            this.planName = '';
            this.provider = null;
            this.canManageBilling = false;
            this.autoRenew = false;
            this.renewalDate = '';
        },

        async loadStatus({ silent = false, preserveOnError = false } = {}) {
            if (!silent) {
                this.loading = true;
            }

            try {
                // ApiClient reads the JWT synchronously and does not retry a 401
                // that carried no token, while the token is minted asynchronously
                // in the same turn this first runs. Without awaiting it, a cold
                // token store shows a subscriber a load error on first paint.
                // The mint carries no abort signal of its own, so it is bounded
                // here: it is the first thing that can block, and an unbounded
                // one would hang the card on its spinner with no way out.
                await withTimeout(getApiToken(), REQUEST_TIMEOUT_MS, 'api token');

                const status = await readJson('/plus-status', { method: 'GET' });

                this.errorMessage = '';
                this.enabled = status.enabled === true;
                this.planName = status.display_name || '';
                this.provider = status.provider || null;
                this.canManageBilling = status.can_manage_billing === true;
                this.autoRenew = status.auto_renew === true;
                this.renewalDate = this.formatDate(status.paid_until);
            } catch (error) {
                console.error('[premiumSubscription] could not load subscription status', error);

                if (preserveOnError) {
                    return;
                }

                // The card must not keep asserting a subscription it can no
                // longer confirm. A reader reaching this point may have just
                // cancelled in the portal, and "Active, renews on 1 August"
                // would be a false statement about their money.
                this.resetSubscription();
                this.errorMessage = messages.loadError;
            } finally {
                this.loading = false;
            }
        },

        /** Wording follows auto-renew: a lapsing subscription expires, it does not renew. */
        get renewalLine() {
            if (!this.renewalDate) {
                return '';
            }

            const template = this.autoRenew ? messages.renewsOn : messages.expiresOn;

            return template.replace('%date%', this.renewalDate);
        },

        get storeNote() {
            if (this.provider === PROVIDER_APPLE) {
                return messages.appStore;
            }
            if (this.provider === PROVIDER_GOOGLE) {
                return messages.playStore;
            }

            return '';
        },

        /**
         * An active member with no billing relationship and no store behind them
         * holds a membership someone granted. There is nothing for them to
         * manage, and saying so is better than ending the card on a blank space
         * where every other member sees a button.
         */
        get grantedNote() {
            if (!this.enabled || this.canManageBilling || this.storeNote) {
                return '';
            }

            return messages.granted;
        },

        /** Reachable when the entitlement lapses between page render and this read. */
        get inactiveMessage() {
            return this.checkoutCompleted ? messages.checkoutPending : messages.inactive;
        },

        /**
         * Feeds the card's one live region. The visible branches carry no ARIA
         * roles of their own, so every async transition is announced here once
         * and only once.
         */
        get statusAnnouncement() {
            if (this.loading) {
                return messages.loading;
            }
            if (this.errorMessage) {
                return this.errorMessage;
            }
            if (!this.enabled) {
                return this.inactiveMessage;
            }

            const details = [this.planName, this.renewalLine].filter(Boolean).join('. ');

            return [
                this.checkoutCompleted ? messages.checkoutSuccess : '',
                details,
            ].filter(Boolean).join(' ');
        },

        /**
         * paid_until is a UTC instant, and a perpetual comp grant has none. The
         * UTC calendar day is rendered so a subscriber west of UTC is not shown
         * a renewal date a day early on the screen they cancel from.
         *
         * Month and weekday names follow the site locale, but the calendar is
         * pinned to Gregorian: this is a billing date, and the Stripe portal it
         * sits next to, the invoice and the card statement all state it in
         * Gregorian. A locale-default Jalali or Buddhist-era year here would
         * leave those readers unable to reconcile the two.
         */
        formatDate(value) {
            if (!value) {
                return '';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return '';
            }

            return date.toLocaleDateString(siteLocale(), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
                calendar: 'gregory',
            });
        },

        /**
         * Re-arms the button if the portal navigation never actually happened.
         *
         * The browser reports a committing navigation but not an abandoned one:
         * pressing Stop or Esc after location.assign() leaves the page exactly
         * as it was, with no event to say so. Holding `working` across the
         * navigation is still right, because each call mints a single-use portal
         * session that invalidates the one before it, so releasing on a plain
         * timer would let a second click cancel the URL the first navigation is
         * still loading. `pagehide` separates the two cases: it fires only when
         * the page is genuinely being torn down. If it has not fired by the time
         * the grace period is up, no navigation is coming, and a button left
         * disabled with no error and no way back is worse than a second session.
         */
        releaseIfNavigationAbandoned() {
            let leaving = false;
            const markLeaving = () => {
                leaving = true;
            };

            window.addEventListener('pagehide', markLeaving, { once: true });

            setTimeout(() => {
                window.removeEventListener('pagehide', markLeaving);
                if (!leaving) {
                    this.working = false;
                }
            }, NAVIGATION_GRACE_MS);
        },

        async manage() {
            if (this.working || !this.canManageBilling) {
                return;
            }

            this.working = true;
            this.errorMessage = '';

            try {
                const session = await readJson('/plus-manage', { method: 'POST' });

                const target = portalUrl(session.url);
                if (target === null) {
                    throw new Error('plus-manage returned an unusable portal url');
                }

                this.releaseIfNavigationAbandoned();
                window.location.assign(target);
            } catch (error) {
                console.error('[premiumSubscription] could not open the billing portal', error);
                this.errorMessage = messages.manageError;
                this.working = false;
            }
        },
    };
}
